import styled from 'styled-components';
import Typography from '../../../../../components/typography/Typography';
import { Section, SectionHeader } from '../../../../../styling/FormStyling';
import { ColumnFlex3 } from '../../../../../styling/Styling';
import { StyledSectionContent } from '../TaskDetailsStep';
import { TaskDetailsStepRelatedTask, TaskRelationType } from './TaskDetailsStepRelatedTask';
import { WorkTaskDTOExtended } from '../../../../../models/TaskType';
import { SubWorkTaskDTO, WorkTaskStatus } from '../../../../../api/api';
import useWorkTaskLock from '../../../../../hooks/useWorkTaskLock';
import { useEffect } from 'react';
import { BookingDetailsOnTask } from './BookingDetalisOnTask';
import { sortDateTime } from '../../../../../utils/dateHandling';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag';

interface Props {
  task?: WorkTaskDTOExtended;
}

export const SubTasks = (props: Props) => {
  const { task } = props;
  const subWorkTasks = task?.subWorkTasks;
  const { releaseWorkTaskLock } = useWorkTaskLock();
  const showRelationshipMap = useFeatureFlag(window._env_.FeatureFlags_RelationshipMap);

  // The relations diagram replaces this list, so it is only shown while that feature is off.
  const showRelatedTasks = !showRelationshipMap && !!task?.relatedTasks && task.relatedTasks.length > 1;

  const splitSubWorkTasks = () => {
    const lausTasks: SubWorkTaskDTO[] = subWorkTasks
      ? subWorkTasks?.filter(
          (sw) => sw.taskTypeId === parseInt(window._env_.LAUS_TASK_ID) && sw.status !== WorkTaskStatus.Canceled
        )
      : [];
    const otherTasks: SubWorkTaskDTO[] = subWorkTasks
      ? subWorkTasks?.filter(
          (sw) => sw.taskTypeId !== parseInt(window._env_.LAUS_TASK_ID) && sw.status !== WorkTaskStatus.Canceled
        )
      : [];

    return { lausTasks, otherTasks };
  };

  useEffect(() => {
    if (task?.subWorkTasks) {
      splitSubWorkTasks();
    }
  }, [task?.subWorkTasks]);

  return (
    <>
      {task && task.subTaskId && (
        <Section>
          <SectionHeader>Hovedopgave</SectionHeader>
          <StyledSectionContent>
            <ColumnFlex3>
              <TaskDetailsStepRelatedTask
                taskId={parseInt(task.id ?? '')}
                taskStatus={task.mainTaskStatus as WorkTaskStatus}
                assignedTechnician={task.mainTaskAssignedToName}
                index={0}
                relationDescription="Tekniker"
                taskRelationType={TaskRelationType.MAINTASK}
                key={task.id}
                releaseWorkTask={() => releaseWorkTaskLock(parseInt(task.subTaskId ?? ''))}
              />
            </ColumnFlex3>
          </StyledSectionContent>
        </Section>
      )}
      {task &&
        !task.subTaskId &&
        (splitSubWorkTasks().lausTasks.length > 0 ||
          splitSubWorkTasks().otherTasks.length > 0 ||
          showRelatedTasks ||
          (task?.bookings && task?.bookings.length > 0)
        )
        && (
          <Section>
            {
              task?.bookings && (
                <SectionHeader>
                  Bookinger
                </SectionHeader>
              )
            }


            <StyledSectionContent>
              <ColumnFlex3>
                {[...(task?.bookings ?? [])]
                  .sort((a, b) => sortDateTime(a.appointmentFrom ?? '', b.appointmentFrom ?? ''))
                  .map((booking, index) => (
                  <BookingDetailsOnTask
                    key={booking.externalBookingId ?? index}
                    index={index}
                    isAppointmentFixed={booking.isAppointmentFixed}
                    assignedTechnicianName={booking.assignedTechnicianName}
                    assignedTechnicianEmail={booking.assignedTechnicianEmail}
                    plannedArrival={booking.plannedArrival}
                    appointmentFrom={booking.appointmentFrom}
                    appointmentTo={booking.appointmentTo}
                    bookingState={booking.bookingState}
                  />
                ))}
              </ColumnFlex3>
            </StyledSectionContent>
            {task?.subWorkTasks && task.subWorkTasks.length > 0 && !task.bookings?.length && (
              <>
                <Typography variant="h6" fontWeight="bold">
                  Underopgaver
                </Typography>
                <StyledSectionContent>
                  <ColumnFlex3>
                    {splitSubWorkTasks().lausTasks.length > 0 &&
                      splitSubWorkTasks().lausTasks.map((sw, index) => {
                        return (
                          <TaskDetailsStepRelatedTask
                            taskId={sw.id}
                            taskStatus={sw.status}
                            assignedTechnician={sw.assignedTechnician}
                            index={index}
                            isSubTask
                            relationDescription="LAUS-mand"
                            taskRelationType={TaskRelationType.SUBTASK}
                            key={sw.id}
                            releaseWorkTask={() => releaseWorkTaskLock(parseInt(task.id ?? ''))}
                          />
                        );
                      })}
                    {splitSubWorkTasks().otherTasks.length > 0 &&
                      splitSubWorkTasks().otherTasks.map((sw, index) => {
                        return (
                          <TaskDetailsStepRelatedTask
                            taskId={sw.id}
                            taskStatus={sw.status}
                            assignedTechnician={sw.assignedTechnician}
                            isSubTask
                            index={index}
                            relationDescription="Tekniker"
                            taskRelationType={TaskRelationType.SUBTASK}
                            key={sw.id}
                            releaseWorkTask={() => releaseWorkTaskLock(parseInt(task.id ?? ''))}
                          />
                        );
                      })}
                  </ColumnFlex3>
                </StyledSectionContent>
              </>
            )}
            {showRelatedTasks && task?.relatedTasks && (
              <>
                <SubSectionHeader variant="h6" fontWeight="bold">
                  Relationsopgaver
                </SubSectionHeader>
                <StyledSectionContent>
                  <ColumnFlex3>
                    {task.relatedTasks.map((rt, index) => {
                      return (
                        <TaskDetailsStepRelatedTask
                          taskId={rt.workTaskId}
                          taskStatus={rt.status}
                          assignedTechnician={rt.assignedTechnician}
                          index={index}
                          relationDescription={
                            rt.workTaskId === parseInt(task?.id ?? '') ? 'Denne opgave' : rt.taskTypeName ?? ''
                          }
                          taskRelationType={TaskRelationType.RELATIONALTASK}
                          key={rt.workTaskId}
                          releaseWorkTask={() => releaseWorkTaskLock(parseInt(task.id ?? ''))}
                          level={rt.level}
                          activeTask={rt.workTaskId === parseInt(task?.id ?? '')}
                        />
                      );
                    })}
                  </ColumnFlex3>
                </StyledSectionContent>
              </>
            )}
          </Section>
        )}
    </>
  );
};

const SubSectionHeader = styled(Typography)`
  margin-top: ${(props) => props.theme.spacing(2)};
`;
